import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import AccessTimeIcon from '@mui/icons-material/AccessTimeOutlined';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOffOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { apiClient } from '@/api/axiosClient';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import TvKpiTile from '@/components/tvDashboard/TvKpiTile';
import TvItemAgingList from '@/components/tvDashboard/TvItemAgingList';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { average } from '@/lib/dashboardAggregations';
import type { StockAgingRow } from '@/types/reports';

const AGE_BANDS = [
  { label: '0-30d', max: 30 },
  { label: '31-60d', max: 60 },
  { label: '61-90d', max: 90 },
  { label: '91-180d', max: 180 },
  { label: '180d+', max: Infinity },
];

export default function StockAgingSlide() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const colors = useCategoryColors();

  const { data: rows = [] } = useQuery({
    queryKey: ['reports', 'stock-aging'],
    queryFn: async () => (await apiClient.get<StockAgingRow[]>('/reports/stock-aging')).data,
    refetchInterval: 60_000,
  });

  const withAge = useMemo(() => rows.filter((r) => r.ageDays != null), [rows]);
  const avgAge = useMemo(() => average(withAge.map((r) => r.ageDays as number)), [withAge]);
  const oldestAge = useMemo(() => (withAge.length ? Math.max(...withAge.map((r) => r.ageDays as number)) : 0), [withAge]);
  const staleCount = useMemo(() => withAge.filter((r) => (r.ageDays as number) > 90).length, [withAge]);

  const ageBandCounts = useMemo(() => {
    const counts = AGE_BANDS.map(() => 0);
    for (const r of withAge) {
      const age = r.ageDays as number;
      const bandIndex = AGE_BANDS.findIndex((b) => age <= b.max);
      counts[bandIndex === -1 ? AGE_BANDS.length - 1 : bandIndex] += 1;
    }
    return counts;
  }, [withAge]);

  const oldestItems = useMemo(
    () =>
      [...withAge]
        .sort((a, b) => (b.ageDays as number) - (a.ageDays as number))
        .slice(0, 8)
        .map((r) => ({
          materialCode: r.materialCode,
          materialName: r.materialName,
          location: r.locationCode ? `${r.warehouseName} • ${r.locationCode}` : r.warehouseName,
          batchNo: r.batchNo,
          quantityOnHand: r.quantityOnHand,
          ageDays: r.ageDays,
        })),
    [withAge],
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        <TvKpiTile label={t('stockAging.trackedLines')} value={rows.length} icon={<Inventory2Icon />} accentColor={colors.inventory} />
        <TvKpiTile label={t('stockAging.avgAge')} value={avgAge > 0 ? `${avgAge.toFixed(0)}d` : '—'} icon={<AccessTimeIcon />} accentColor={colors.inventory} />
        <TvKpiTile label={t('stockAging.oldestAge')} value={oldestAge > 0 ? `${oldestAge}d` : '—'} icon={<HistoryToggleOffIcon />} accentColor={colors.production} />
        <TvKpiTile label={t('stockAging.staleCount')} value={staleCount} icon={<ReportProblemOutlinedIcon />} accentColor={colors.adjustments} />
      </Box>
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '0.85fr 1.5fr', gap: 2, minHeight: 0 }}>
        <ChartCard title={t('stockAging.distributionTitle')} subtitle={t('stockAging.distributionSubtitle')} height={420}>
          <DashboardBarChart
            layout="horizontal"
            categories={AGE_BANDS.map((b) => b.label)}
            series={[{ label: t('stockAging.lineCount'), data: ageBandCounts, color: colors.inventory }]}
            height={420}
            legendPosition="bottom"
          />
        </ChartCard>
        <TvItemAgingList
          title={t('stockAging.oldestItemsTitle')}
          subtitle={t('stockAging.oldestItemsSubtitle')}
          items={oldestItems}
          columnLabels={{
            material: tr('columns.materialCode'),
            location: tr('columns.warehouse'),
            batch: tr('columns.batch'),
            qty: tr('columns.onHand'),
            age: t('stockAging.ageColumn'),
          }}
        />
      </Box>
    </Box>
  );
}
